import React, { useState, useEffect } from 'react';
import {
  Target, Plus, Search, Clock, CheckCircle, AlertTriangle,
  Loader2, X, Edit, Trash2, Calendar, User, ChevronDown
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import ConfirmDialog from '../shared/ConfirmDialog';

const priorityColors = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-blue-100 text-blue-700 border-blue-200',
};
const statusColors = {
  pending: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-50 text-gray-500',
};
const priorities = ['critical', 'high', 'medium', 'low'];
const statuses = ['pending', 'in_progress', 'completed', 'overdue', 'cancelled'];

const emptyForm = { title: '', description: '', priority: 'medium', assigned_to_name: '', department: '', due_date: '', event_id: '', action_type: 'corrective' };

const ActionPlansPage = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [events, setEvents] = useState([]);

  const loadActions = () => {
    setLoading(true);
    const params = filter !== 'all' ? `?status=${filter}` : '';
    api.get(`/api/corrective-actions${params}`).then(setActions).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  };

  useEffect(() => { loadActions(); }, [filter]);
  useEffect(() => {
    api.get('/api/safety-events').then(setEvents).catch(() => {});
  }, []);

  const handleCreate = async () => {
    if (!form.title) { toast.error('Title is required'); return; }
    setSubmitting(true);
    try {
      await api.post('/api/corrective-actions', form);
      toast.success('Action plan created');
      setShowCreate(false);
      setForm(emptyForm);
      loadActions();
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const openEdit = (action) => {
    setEditForm({
      title: action.title || '',
      description: action.description || '',
      priority: action.priority || 'medium',
      status: action.status || 'pending',
      assigned_to_name: action.assigned_to_name || '',
      department: action.department || '',
      due_date: action.due_date ? action.due_date.split('T')[0] : '',
      completion_notes: action.completion_notes || '',
    });
    setEditingAction(action);
    setShowEdit(true);
  };

  const handleUpdate = async () => {
    setSubmitting(true);
    try {
      await api.put(`/api/corrective-actions/${editingAction.id}`, editForm);
      toast.success('Action updated');
      setShowEdit(false);
      loadActions();
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/api/corrective-actions/${deleteTarget.id}`);
      toast.success('Action deleted');
      setDeleteTarget(null);
      loadActions();
    } catch (e) { toast.error(e.message); } finally { setDeleting(false); }
  };

  const quickStatusChange = async (action, newStatus) => {
    try {
      await api.put(`/api/corrective-actions/${action.id}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      loadActions();
    } catch (e) { toast.error(e.message); }
  };

  const filteredActions = actions.filter(a =>
    a.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.assigned_to_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.department?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const counts = { pending: 0, in_progress: 0, completed: 0, overdue: 0 };
  actions.forEach(a => { if (counts[a.status] !== undefined) counts[a.status]++; });

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const diff = Math.ceil((new Date(dueDate) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  return (
    <div className="space-y-6" data-testid="action-plans-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Action Plans</h1>
          <p className="text-gray-500 text-sm mt-1">Track corrective and preventive actions to completion</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setShowCreate(true); }} className="btn-primary gap-2" data-testid="create-action-btn">
          <Plus className="w-4 h-4" /> New Action Plan
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Pending', value: counts.pending, icon: Clock, color: 'text-gray-600' },
          { label: 'In Progress', value: counts.in_progress, icon: Target, color: 'text-blue-600' },
          { label: 'Completed', value: counts.completed, icon: CheckCircle, color: 'text-green-600' },
          { label: 'Overdue', value: counts.overdue, icon: AlertTriangle, color: 'text-red-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <s.icon className={`w-4 h-4 ${s.color}`} />
              <span className="text-sm text-gray-500">{s.label}</span>
            </div>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search actions..." className="pl-9" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-testid="search-actions-input" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'pending', 'in_progress', 'completed', 'overdue'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === f ? 'bg-[var(--niko-teal)] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid={`filter-${f}`}
            >{f === 'all' ? 'All' : f.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</button>
          ))}
        </div>
      </div>

      {/* Actions List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[var(--niko-teal)]" /></div>
      ) : (
        <div className="space-y-3">
          {filteredActions.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-gray-400">No action plans found</div>
          ) : filteredActions.map(action => {
            const daysLeft = getDaysUntilDue(action.due_date);
            return (
              <div key={action.id} className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-all group" data-testid="action-item">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium border ${priorityColors[action.priority] || 'bg-gray-100'}`}>{action.priority}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[action.status] || 'bg-gray-100'}`}>{(action.status || '').replace('_', ' ')}</span>
                      {action.action_type && <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-600">{action.action_type}</span>}
                    </div>
                    <h3 className="font-medium text-gray-900">{action.title}</h3>
                    {action.description && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{action.description}</p>}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      {action.assigned_to_name && (
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{action.assigned_to_name}</span>
                      )}
                      {action.department && <span>{action.department}</span>}
                      {action.event_title && <span className="text-[var(--niko-teal)]">Event: {action.event_title}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {action.due_date && (
                      <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${
                        daysLeft !== null && daysLeft < 0 ? 'bg-red-50 text-red-600' :
                        daysLeft !== null && daysLeft <= 3 ? 'bg-yellow-50 text-yellow-600' :
                        'bg-gray-50 text-gray-500'
                      }`}>
                        <Calendar className="w-3 h-3" />
                        {daysLeft !== null && daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` :
                         daysLeft !== null && daysLeft === 0 ? 'Due today' :
                         daysLeft !== null ? `${daysLeft}d left` :
                         new Date(action.due_date).toLocaleDateString()}
                      </div>
                    )}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {action.status === 'pending' && (
                        <button onClick={() => quickStatusChange(action, 'in_progress')} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg" title="Start"><Target className="w-3.5 h-3.5" /></button>
                      )}
                      {(action.status === 'in_progress' || action.status === 'pending') && (
                        <button onClick={() => quickStatusChange(action, 'completed')} className="p-1.5 text-green-500 hover:bg-green-50 rounded-lg" title="Complete"><CheckCircle className="w-3.5 h-3.5" /></button>
                      )}
                      <button onClick={() => openEdit(action)} className="p-1.5 text-gray-400 hover:text-[var(--niko-teal)] hover:bg-[var(--niko-teal)]/10 rounded-lg" data-testid="edit-action-btn"><Edit className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setDeleteTarget(action)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid="delete-action-btn"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreate(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-action-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">New Action Plan</h2>
              <button onClick={() => setShowCreate(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><Label>Title *</Label><Input className="mt-1" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="action-title-input" /></div>
              <div><Label>Description</Label><Textarea className="mt-1" rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Priority</Label>
                  <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}>
                    {priorities.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Type</Label>
                  <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.action_type} onChange={e => setForm({...form, action_type: e.target.value})}>
                    <option value="corrective">Corrective</option>
                    <option value="preventive">Preventive</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Assigned To</Label><Input className="mt-1" value={form.assigned_to_name} onChange={e => setForm({...form, assigned_to_name: e.target.value})} /></div>
                <div><Label>Department</Label><Input className="mt-1" value={form.department} onChange={e => setForm({...form, department: e.target.value})} /></div>
              </div>
              <div><Label>Due Date</Label><Input type="date" className="mt-1" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} /></div>
              {events.length > 0 && (
                <div>
                  <Label>Linked Safety Event (optional)</Label>
                  <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.event_id} onChange={e => setForm({...form, event_id: e.target.value})}>
                    <option value="">None</option>
                    {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex gap-3">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleCreate} disabled={submitting} className="flex-1 btn-primary" data-testid="submit-action-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Create Action
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && editingAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowEdit(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-action-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Edit Action Plan</h2>
              <button onClick={() => setShowEdit(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><Label>Title</Label><Input className="mt-1" value={editForm.title} onChange={e => setEditForm({...editForm, title: e.target.value})} /></div>
              <div><Label>Description</Label><Textarea className="mt-1" rows={3} value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Priority</Label>
                  <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={editForm.priority} onChange={e => setEditForm({...editForm, priority: e.target.value})}>
                    {priorities.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Status</Label>
                  <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={editForm.status} onChange={e => setEditForm({...editForm, status: e.target.value})}>
                    {statuses.map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Assigned To</Label><Input className="mt-1" value={editForm.assigned_to_name} onChange={e => setEditForm({...editForm, assigned_to_name: e.target.value})} /></div>
                <div><Label>Department</Label><Input className="mt-1" value={editForm.department} onChange={e => setEditForm({...editForm, department: e.target.value})} /></div>
              </div>
              <div><Label>Due Date</Label><Input type="date" className="mt-1" value={editForm.due_date} onChange={e => setEditForm({...editForm, due_date: e.target.value})} /></div>
              {editForm.status === 'completed' && (
                <div><Label>Completion Notes</Label><Textarea className="mt-1" rows={2} value={editForm.completion_notes} onChange={e => setEditForm({...editForm, completion_notes: e.target.value})} /></div>
              )}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex gap-3">
              <Button variant="outline" onClick={() => setShowEdit(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleUpdate} disabled={submitting} className="flex-1 btn-primary" data-testid="submit-edit-action-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Update
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} loading={deleting}
        title="Delete Action Plan" message={`Are you sure you want to delete "${deleteTarget?.title}"? This cannot be undone.`} />
    </div>
  );
};

export default ActionPlansPage;
